import { Plus } from 'lucide-react'
import { useRef, useState } from 'react'

import { useUpdateParcel } from '../api/client'
import { de } from '../i18n/de'

/** Mirrors `description`'s limit in the backend schema. */
const MAX_DESCRIPTION_LENGTH = 512

/**
 * What is in the parcel, editable inline while it is still unknown.
 *
 * Same idea as `ProjectChip`: an empty field is an open question on the board, so it is
 * answered where it is seen. A description that exists is edited in the drawer, where a
 * stray click on the row cannot change it.
 */
export function DescriptionChip({
  parcelId,
  description,
}: {
  parcelId: number
  description: string | null
}) {
  const updateParcel = useUpdateParcel()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const finished = useRef(false)

  if (description) {
    return (
      <span className="min-w-0 truncate font-medium text-slate-900 dark:text-slate-100">
        {description}
      </span>
    )
  }

  const save = () => {
    // Enter saves and unmounts the input, whose blur then fires with the same draft.
    if (finished.current) return
    finished.current = true
    const value = draft.trim()
    setEditing(false)
    setDraft('')
    if (value) updateParcel.mutate({ id: parcelId, patch: { description: value } })
  }

  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        maxLength={MAX_DESCRIPTION_LENGTH}
        placeholder={de.parcel.description}
        aria-label={de.parcel.description}
        disabled={updateParcel.isPending}
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={save}
        onKeyDown={(event) => {
          event.stopPropagation()
          if (event.key === 'Enter') save()
          if (event.key === 'Escape') {
            finished.current = true
            setDraft('')
            setEditing(false)
          }
        }}
        className="w-48 max-w-full min-w-0 rounded border border-slate-300 bg-white px-1.5 py-0.5 text-sm dark:border-slate-600 dark:bg-slate-800"
      />
    )
  }

  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation()
        finished.current = false
        setEditing(true)
      }}
      title={de.parcel.description}
      className="relative after:absolute after:-inset-1.5 after:content-[''] inline-flex items-center gap-1 rounded-full border border-dashed border-slate-300 px-2 py-0.5 text-xs text-slate-500 transition hover:border-slate-400 hover:text-slate-600 dark:border-slate-600 dark:text-slate-400 dark:hover:text-slate-300"
    >
      <Plus className="h-3 w-3 shrink-0" aria-hidden />
      <span>{de.parcel.description}</span>
    </button>
  )
}
